import { Badge } from '@/components/ui';
import { ORDER_STATUS_LABEL, QUOTE_STATUS_LABEL } from '@/lib/types';
import type { OrderEvent, OrderStatus, QuoteStatus } from '@/lib/types';

const ORDER_TONE: Record<OrderStatus, 'neutral' | 'ok' | 'warn' | 'danger' | 'brand'> = {
  pending_payment: 'warn',
  expired: 'neutral',
  paid: 'brand',
  in_production: 'brand',
  quality_check: 'brand',
  shipped: 'ok',
  delivered: 'ok',
  cancelled: 'neutral',
  refunded: 'warn',
};

const QUOTE_TONE: Record<QuoteStatus, 'neutral' | 'ok' | 'warn' | 'danger' | 'brand'> = {
  submitted: 'warn',
  priced: 'brand',
  proof_sent: 'brand',
  approved: 'ok',
  deposit_paid: 'ok',
  in_production: 'brand',
  quality_check: 'brand',
  shipped: 'ok',
  completed: 'ok',
  declined: 'danger',
  cancelled: 'neutral',
};

export function OrderStatusBadge({ status }: { status: OrderStatus }) {
  return <Badge tone={ORDER_TONE[status]}>{ORDER_STATUS_LABEL[status]}</Badge>;
}

export function QuoteStatusBadge({ status }: { status: QuoteStatus }) {
  return <Badge tone={QUOTE_TONE[status]}>{QUOTE_STATUS_LABEL[status]}</Badge>;
}

const dateFmt = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

export function formatDate(iso: string): string {
  return dateFmt.format(new Date(iso));
}

/**
 * The order timeline shown in both clients. Every pipeline transition writes an
 * event (AC-3.5), so this list is the audit trail, not a decoration.
 */
export function StatusTimeline({ events }: { events: OrderEvent[] }) {
  const ordered = [...events].sort((a, b) => a.created_at.localeCompare(b.created_at));

  return (
    <ol className="relative space-y-6 pl-7">
      <span
        aria-hidden
        className="absolute bottom-2 left-[7px] top-2 w-px bg-line-strong"
      />
      {ordered.map((event, i) => {
        const latest = i === ordered.length - 1;
        return (
          <li key={event.id} className="relative">
            <span
              aria-hidden
              className={
                'absolute -left-7 top-1 grid size-[15px] place-items-center rounded-full border-2 ' +
                (latest ? 'border-brand bg-brand' : 'border-line-strong bg-panel')
              }
            />
            <p className="text-[15px] font-bold">{ORDER_STATUS_LABEL[event.status]}</p>
            <p className="text-sm text-muted">{formatDate(event.created_at)}</p>
            {event.note && <p className="mt-1 text-[15px]">{event.note}</p>}
          </li>
        );
      })}
    </ol>
  );
}
